// Single permission hook for the whole console, so one image serves deployments that have
// adopted RBAC and deployments that have not.
//
//   REACT_APP_AUTH_MODE=rbac   delegate to the RBAC client - per-user grants, live evaluation
//   REACT_APP_AUTH_MODE=local  evaluate against settings.roles (see urnRoles.js)
//
// Call sites are identical either way and keep the shape they already used:
//
//   const canDelete = useCan('URN:TWX:ESB;;Alarms:Types:Delete', 'write').allow;
//
// The implementation is chosen ONCE at module load, not per render. config.authMode comes
// from window._env_ / process.env and cannot change during the app's lifetime, so the same
// hook runs on every render and the rules of hooks hold. Selecting inside the hook body
// would be a conditional hook call.

import { useContext } from 'react';
import { useCan as useRbacCan } from '@e-magic-inc/rbac-client/react';

import ApplicationContext from '../contexts/application';
import { config } from '../libs/config';
import { roleAllows } from './urnRoles';

/**
 * Role-backed evaluation. Mirrors the RBAC hook's return shape so call sites do not care
 * which one they got.
 */
function useLocalCan(urn, action) {
    const { settings } = useContext(ApplicationContext);
    return {
        // The action is carried through for the one distinction roleAllows draws with it,
        // between an operator running the system and an operator deleting its configuration.
        allow: roleAllows(urn, settings?.roles, action),
        // The local check is synchronous and cannot fail, so it is never "unavailable".
        unavailable: false,
    };
}

/**
 * RBAC-backed evaluation.
 *
 * A denial and an outage both surface as allow=false from the RBAC client, which is
 * miserable to diagnose: an unreachable RBAC service renders a console with every menu
 * hidden, indistinguishable from a permissions problem. `unavailable` is carried here so
 * callers can tell the two apart and show an error instead of an empty shell.
 *
 * TODO: the RBAC client does not currently expose a reachability signal, so this reports
 * false. Wire it to the client's error/loading state once that is available - the shape is
 * in place so call sites do not need changing again.
 */
function useRbacBackedCan(urn, action) {
    const result = useRbacCan(urn, action);
    return {
        allow: Boolean(result?.allow),
        unavailable: false,
    };
}

const useCanImpl = config.authMode === 'rbac' ? useRbacBackedCan : useLocalCan;

export function useCan(urn, action) {
    return useCanImpl(urn, action);
}

export default useCan;
